import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { safeGet } from "@/lib/api";
import { useHead } from "@/lib/useHead";
import { pageSchema } from "@/lib/schema";

const BLOG_DESC = "Articles for beginners on spotting market shifts early and turning them into a first business.";

export const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const ArticleCard = ({ a, featured }) => (
  <Link to={`/blog/${a.slug}`} data-testid={`blog-card-${a.slug}`}
    className={`group flex h-full flex-col overflow-hidden border border-line bg-[#0b0b0b] transition-colors hover:border-zinc-500 ${featured ? "md:col-span-2 md:flex-row" : ""}`}>
    {a.image_url && (
      <div className={`relative overflow-hidden ${featured ? "aspect-[16/10] md:aspect-auto md:w-3/5" : "aspect-[16/10]"}`}>
        <img src={a.image_url} alt={a.title || ""} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
    )}
    <div className={`flex flex-1 flex-col p-6 md:p-8 ${featured ? "md:justify-center" : ""}`}>
      <p className="eyebrow">{[a.category, a.read_time].filter(Boolean).join(" · ") || "Article"}</p>
      <h2 className={`font-display mt-4 font-medium tracking-tight text-white ${featured ? "text-2xl md:text-4xl" : "text-xl md:text-2xl"}`}>{a.title}</h2>
      {(a.subtitle || a.meta_description) && <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-zinc-300">{a.subtitle || a.meta_description}</p>}
      <div className="mt-auto flex items-center justify-between pt-6 text-xs text-zinc-400">
        <span>{formatDate(a.published_at)}</span>
        <ArrowUpRight size={18} className="text-white transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
      </div>
    </div>
  </Link>
);

export default function Blog() {
  const [articles, setArticles] = useState(null);
  useHead({
    title: "Blog | First Mover OS",
    description: BLOG_DESC,
    path: "/blog",
    jsonLd: pageSchema("Blog", "Blog", "/blog", BLOG_DESC),
  });
  useEffect(() => {
    safeGet("/articles", null).then((d) => setArticles(Array.isArray(d?.articles) ? d.articles : []));
  }, []);

  return (
    <main data-testid="blog-page" className="pt-28 md:pt-36">
      <div className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-36">
        <p className="eyebrow">The blog</p>
        <h1 className="font-display mt-5 text-4xl font-medium tracking-tight text-white sm:text-5xl lg:text-6xl">
          Notes for first movers.
        </h1>
        <div className="mt-14 grid gap-6 md:grid-cols-2" data-testid="blog-list">
          {articles === null && [0, 1].map((i) => <div key={i} className="h-80 animate-pulse border border-line bg-white/[0.02]" />)}
          {articles?.length === 0 && <p className="text-zinc-400" data-testid="blog-empty">No articles yet. Check back soon.</p>}
          {articles?.map((a, i) => <ArticleCard key={a.slug} a={a} featured={i === 0} />)}
        </div>
      </div>
    </main>
  );
}
